import {
  useMemo, useState, useRef, useEffect,
} from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table';
import { useGetEvaluationsForLecturer } from '@/common/api/endpoints/evaluations/evaluations';
import type { PendingEvaluation } from '@/common/api/models';
import { useApiErrorReporter } from '@/common/utils/apiErrorReporter';

/**
 * Custom hook for managing the Evaluation Table state using TanStack Table.
 * Provides sorting, filtering, and pagination with server-side data fetching.
 *
 * @param selectedModuleId - Optional module ID to filter evaluations
 * @returns Table instance and state management functions
 */
export const useEvaluationTable = (selectedModuleId?: string | null) => {
  const { reportApiError } = useApiErrorReporter();
  const hasReportedError = useRef(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'submittedAt', desc: false }, // Default: Oldest first
  ]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
    { id: 'state', value: 'submitted' }, // Default: Show only submitted
  ]);
  const [globalFilter, setGlobalFilter] = useState('');

  // Build API parameters
  const apiParams = useMemo(() => {
    const params: {
      moduleId?: string | null;
      page: number;
      pageSize: number;
      sort?: string;
      filter?: string;
    } = {
      moduleId: selectedModuleId,
      page,
      pageSize,
    };

    // Add sorting (suffix "-" for descending)
    if (sorting.length > 0) {
      const sortField = sorting[0];
      params.sort = sortField.desc ? `${sortField.id}-` : sortField.id;
    }

    // Build filter string in "field:value field2:value2" format
    const filterParts: string[] = [];

    // Add column filters (e.g., state:submitted|processing for pending tab)
    columnFilters.forEach((filter) => {
      if (filter.id === 'state' && filter.value === 'submitted') {
        filterParts.push('state:submitted|processing');
      } else {
        filterParts.push(`${filter.id}:${String(filter.value)}`);
      }
    });

    // Add global search filter
    if (globalFilter && globalFilter.trim()) {
      // If user typed field:value syntax, use as-is
      // Otherwise, default to student search
      if (globalFilter.includes(':')) {
        filterParts.push(globalFilter.trim());
      } else {
        filterParts.push(`student:${globalFilter.trim()}`);
      }
    }

    // Combine all filters into single string (comma-separated format)
    if (filterParts.length > 0) {
      params.filter = filterParts.join(',');
    }

    return params;
  }, [selectedModuleId, page, pageSize, sorting, columnFilters, globalFilter]);

  // Reset error flag when params change (new search/filter)
  const apiParamsKey = JSON.stringify(apiParams);
  useEffect(() => {
    hasReportedError.current = false;
  }, [apiParamsKey]);

  // Fetch data from API
  const {
    data: response,
    isLoading,
    isError,
    error,
  } = useGetEvaluationsForLecturer(apiParams, {
    query: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      // Keep the desk live: processing → graded, and newly submitted rows.
      refetchInterval: 10_000,
      placeholderData: (prev) => prev, // Keep previous data while loading
      retry: 1, // Only retry once instead of default 3 times
      retryDelay: 1000, // Wait 1 second before retrying
    },
  });

  // Report error only once after all retries are exhausted
  // Use a separate effect to ensure it runs after query settles
  useEffect(() => {
    if (isError && error && !hasReportedError.current) {
      hasReportedError.current = true;
      reportApiError(error, 'Failed to load evaluations');
    }
  }, [isError, error, reportApiError]);

  // Extract data and pagination from response
  const evaluations = response?.data?.data || [];
  const pagination = response?.data?.pagination;
  const totalItems = pagination?.total || 0;
  const totalPages = pagination ? Math.ceil(pagination.total / pagination.pageSize) : 1;

  // Define columns for TanStack Table
  const columns = useMemo<ColumnDef<PendingEvaluation>[]>(
    () => [
      {
        accessorKey: 'student',
        id: 'student',
        header: 'Student',
        enableSorting: true,
      },
      {
        accessorKey: 'module',
        id: 'module',
        header: 'Module',
        enableSorting: true,
      },
      {
        accessorKey: 'dueDate',
        id: 'dueDate',
        header: 'Due Date',
        enableSorting: false, // Calculated property, not sortable
      },
      {
        accessorKey: 'submittedAt',
        id: 'submittedAt',
        header: 'Submitted',
        enableSorting: true,
      },
      {
        id: 'state',
        accessorKey: 'state',
        header: 'Status',
        enableSorting: true,
      },
      {
        id: 'grade',
        header: 'Grade',
        enableSorting: true,
        accessorFn: (row) => row.grade || null,
      },
    ],
    [],
  );

  // Create table instance
  const table = useReactTable({
    data: evaluations,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true, // Server-side pagination
    manualSorting: true, // Server-side sorting
    manualFiltering: true, // Server-side filtering
    pageCount: totalPages,
  });

  return {
    table,
    evaluations,
    isLoading,
    isError,
    error,
    // Pagination state
    page,
    setPage,
    pageSize,
    setPageSize,
    totalItems,
    totalPages,
    // Filter state
    globalFilter,
    setGlobalFilter,
    columnFilters,
    setColumnFilters,
    // Sorting state
    sorting,
    setSorting,
  };
};

export default useEvaluationTable;
